/*
 * // Created on 2024-02-14
 * // Created by Vitaut Tryputsin
 * //
 * // Copyright (c) 2024 OPEN CASCADE SAS
 * //
 * // This file is part of commercial software by OPEN CASCADE SAS,
 * // furnished in accordance with the terms and conditions of the contract
 * // and with the inclusion of this copyright notice.
 * // This file or any part thereof may not be provided or otherwise
 * // made available to any third party.
 * //
 * // No ownership title to the software is transferred hereby.
 * //
 * // OPEN CASCADE SAS makes no representation or warranties with respect to the
 * // performance of this software, and specifically disclaims any responsibility
 * // for any damages, special or consequential, connected with its use.
 *
 */
import { StemHeadUtilities } from './stem-head-utilities.js';
import { Vec3Tools } from '../../../core/geometry/vec3-tools.js';
import { Mat4Tools } from '../../../core/geometry/mat4-tools.js';
export class PelvisTiltUtils {
    /**
   * Get anterior pelvic plane
   * @param {WasmVector3d} thePelvisRightASISLandmark - right ASIS landmark
   * @param {WasmVector3d} thePelvisLeftASISLandmark - left ASIS landmark
   * @param {WasmVector3d} thePelvisRightPubicSymphysisLandmark - right pubic symphysis landmark
   * @param {WasmVector3d} thePelvisLeftPubicSymphysisLandmark - left pubic symphysis landmark
   * @return {number} anterior pelvic plane
   */
    static getAPP(thePelvisRightASISLandmark, thePelvisLeftASISLandmark, thePelvisRightPubicSymphysisLandmark, thePelvisLeftPubicSymphysisLandmark) {
        const aSymph = Vec3Tools.multiply(Vec3Tools.add(thePelvisRightPubicSymphysisLandmark, thePelvisLeftPubicSymphysisLandmark), 0.5);
        const aPlane = StemHeadUtilities.get3PointPlane(thePelvisRightASISLandmark, thePelvisLeftASISLandmark, aSymph);
        return aPlane;
    }
    /**
       * Get relative pelvis tilt transform
       * @param {WasmVector3d} thePelvisRightASISLandmark - right ASIS landmark
       * @param {WasmVector3d} thePelvisLeftASISLandmark - left ASIS landmark
       * @param {number} theAngleFrom - angle from in radians
       * @param {number} theAngleTo - angle to in radians
       * @return {WasmMatrix4d} relative pelvis tilt transform
      */
    static getRelativePelvisTiltTransform(thePelvisRightASISLandmark, thePelvisLeftASISLandmark, theAngleFrom, theAngleTo) {
        const aDir = Vec3Tools.subtract(thePelvisLeftASISLandmark, thePelvisRightASISLandmark);
        const aCurTransform = Mat4Tools.buildRotationMatrix(thePelvisRightASISLandmark, aDir, theAngleFrom);
        const anExpectedTransform = Mat4Tools.buildRotationMatrix(thePelvisRightASISLandmark, aDir, theAngleTo);
        const aRelativeTransRotation = Mat4Tools.relativeTransform(aCurTransform, anExpectedTransform);
        return aRelativeTransRotation;
    }
    /**
     * Get pelvis tilt angle
     * @param {WasmVector3d} thePelvisRightASISLandmark - right ASIS landmark
     * @param {WasmVector3d} thePelvisLeftASISLandmark - left ASIS landmark
     * @param {WasmVector3d} thePelvisRightPubicSymphysisLandmark - right pubic symphysis landmark
     * @param {WasmVector3d} thePelvisLeftPubicSymphysisLandmark - left pubic symphysis landmark
     * @returns {number} angle in radians
     * */
    static getPelvisTilt(thePelvisRightASISLandmark, thePelvisLeftASISLandmark, thePelvisRightPubicSymphysisLandmark, thePelvisLeftPubicSymphysisLandmark) {
        const aPlane = this.getAPP(thePelvisRightASISLandmark, thePelvisLeftASISLandmark, thePelvisRightPubicSymphysisLandmark, thePelvisLeftPubicSymphysisLandmark);
        const aPlane2 = StemHeadUtilities.get3PointPlane(thePelvisLeftASISLandmark, thePelvisRightASISLandmark, Vec3Tools.add(thePelvisRightASISLandmark, { x: 0, y: 0, z: 1 }));
        const aNormal = aPlane.normal;
        let anAngle = Vec3Tools.angle(aPlane.normal, aPlane2.normal);
        if (aNormal.z < 0) {
            anAngle = -anAngle;
        }
        return anAngle;
    }
}
